import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import BasicText from "./BasicText";
import ContentTitle from "./ContentTitle";
import PointItem from "./PointItem";

import { Smoking } from "../../common/classes/SonicFacility";
import { ImageSourceSetData } from "../../common/classes/ImageSourceSet";
import { SNSData } from "../../common/classes/SNSInfo";

import { parsePrefecture, getDefaultImage } from "../../utils/GeneralUtils";

import styles from "./FacilityDetail.module.scss";
import UserContact from "./UserContact";
import ContactBanner from "./ContactBanner";
import FacilityContact from "./FacilityContact";
import YouTubeChannelContainer from "../media/youtube/YouTubeChannelContainer";
import { SonicRelatedFacilityData } from "../../common/classes/SonicRelatedFacility";
import FacilityComparison from "./FacilityComparison";

type Props = {
  id: string;
  name: string;
  imgs?: ImageSourceSetData[];
  map?: string;
  prefecture?: string;
  address?: string;
  tel?: string;
  introduction?: string;
  businessDays?: string;
  hostEvents?: string;
  foodService?: string;
  price?: string;
  reservation?: string;
  seats?: string;
  officialSite?: string;
  sns?: Pick<SNSData, "twitter" | "instagram" | "facebook" | "youtube">;
  smoking?: Smoking;
  access?: string;
  machines?: string;
  relatedFacilities?: Array<SonicRelatedFacilityData>;
};

const FacilityDetail: React.FC<Props> = ({
  id,
  name,
  imgs,
  map,
  prefecture,
  address,
  tel,
  introduction,
  businessDays,
  hostEvents,
  foodService,
  price,
  reservation,
  seats,
  officialSite,
  sns,
  smoking,
  access,
  machines,
  relatedFacilities,
}) => {
  const [imgNum, setImgNum] = useState<number>(0);

  const scrollToTarget = (target: string) => {
    const el = document.getElementById(target);

    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className={styles["f-facilityDetail"]}>
      <div className={styles["f-facilityDetail__head"]}>
        <button onClick={() => history.back()}>
          <FontAwesomeIcon icon={["fas", "angle-left"]} />
          BACK
        </button>
      </div>
      <div className={`${styles["f-facilityDetail__info"]}`}>
        <div className={styles["f-facilityDetail__mainImg"]}>
          <div className={styles["f-facilityDetail__mainImg-img"]}>
            {prefecture && (
              <span>{parsePrefecture(prefecture).toLocaleUpperCase()}</span>
            )}
            <img
              src={
                imgs && imgs.length ? imgs[imgNum].full.url : getDefaultImage()
              }
              alt={name}
            />
          </div>
          {sns &&
            (sns.facebook || sns.instagram || sns.twitter || sns.youtube) && (
              <div className={styles["f-facilityDetail__mainImg-snsBox"]}>
                <div
                  className={
                    styles["f-facilityDetail__mainImg-snsBox__wrapper"]
                  }
                >
                  {sns.twitter && (
                    <div
                      className={
                        styles["f-facilityDetail__mainImg-snsBox__item"]
                      }
                    >
                      <a
                        href={sns.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["twitter"]}
                      >
                        <FontAwesomeIcon icon={["fab", "x-twitter"]} />
                      </a>
                    </div>
                  )}
                  {sns.instagram && (
                    <div
                      className={
                        styles["f-facilityDetail__mainImg-snsBox__item"]
                      }
                    >
                      <a
                        href={sns.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["instagram"]}
                      >
                        <FontAwesomeIcon icon={["fab", "instagram"]} />
                      </a>
                    </div>
                  )}
                  {sns.facebook && (
                    <div
                      className={
                        styles["f-facilityDetail__mainImg-snsBox__item"]
                      }
                    >
                      <a
                        href={sns.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["facebook"]}
                      >
                        <FontAwesomeIcon icon={["fab", "facebook"]} />
                      </a>
                    </div>
                  )}
                  {sns.youtube && (
                    <div
                      className={
                        styles["f-facilityDetail__mainImg-snsBox__item"]
                      }
                    >
                      <a
                        href={sns.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["youtube"]}
                      >
                        <FontAwesomeIcon icon={["fab", "youtube"]} />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
        {imgs && imgs.length ? (
          <div className={styles["f-facilityDetail__imgList"]}>
            {imgs.map((img, i) => (
              <button
                key={img.id}
                onClick={() => {
                  setImgNum(i);
                }}
              >
                <img src={img.thumbnail.url} alt={name} />
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className={styles["f-facilityDetail__info"]}>
        <h1 className={styles["f-facilityDetail__info-title"]}>{name}</h1>
        {officialSite && (
          <div className={styles["f-facilityDetail__info-hp"]}>
            <a href={officialSite} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={["fas", "external-link-alt"]} />
              <span>オフィシャルHP</span>
            </a>
          </div>
        )}
        <div className={styles["inner"]}>
          <div className={styles["f-facilityDetail__info-index"]}>
            <button
              onClick={() => scrollToTarget("pageLink-about")}
              className={styles["f-facilityDetail__info-index__link"]}
            >
              施設概要
            </button>
            <button
              onClick={() => scrollToTarget("pageLink-point")}
              className={styles["f-facilityDetail__info-index__link"]}
            >
              施設特徴
            </button>
            <button
              onClick={() => scrollToTarget("pageLink-message")}
              className={styles["f-facilityDetail__info-index__link"]}
            >
              施設紹介
            </button>
          </div>
        </div>
        <div
          className={styles["f-facilityDetail__info-about"]}
          id="pageLink-about"
        >
          <div className={styles["inner"]}>
            <ContentTitle
              icon={["fas", "home-alt"]}
              title="施設概要"
              subTitle="ABOUT"
            />
            {address && (
              <BasicText title="住所" text={address}>
                {map ? (
                  <a
                    href={map}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles["f-facilityDetail__info-about-map"]}
                  >
                    <FontAwesomeIcon icon={["fas", "map-marker-alt"]} />
                    <span>MAP</span>
                  </a>
                ) : undefined}
              </BasicText>
            )}
            {access && <BasicText title="アクセス" text={access} />}
            {businessDays && <BasicText title="営業日時" text={businessDays} />}
            {price && <BasicText title="料金" text={price} />}
            {tel && <BasicText title="TEL" text={tel} />}
            {seats && <BasicText title="座席数" text={seats} />}
            {machines && <BasicText title="設備/スペック" text={machines} />}
          </div>
        </div>
      </div>
      <div
        className={styles["f-facilityDetail__info-point"]}
        id="pageLink-point"
      >
        <div
          className={`${styles["f-facilityDetail__info-point__frame"]} ${styles["top"]}`}
        ></div>
        <div className={styles["inner"]}>
          <ContentTitle
            icon={["fas", "sparkles"]}
            title="施設特徴"
            subTitle="POINT"
          />
          <div className={styles["f-facilityDetail__info-point__box"]}>
            <PointItem
              text={"イベント\n開催NG"}
              activeText={"イベント\n開催OK"}
              isActive={hostEvents === "event_true"}
            />
            <PointItem
              text={"フード\n提供なし"}
              activeText={"フード\n提供あり"}
              isActive={foodService === "food_true"}
            />
            <PointItem
              text={"ご予約\n利用NG"}
              activeText={"ご予約\n利用OK"}
              isActive={reservation === "reservation_true"}
            />
            <PointItem
              text={smoking === Smoking.Separate ? "分煙" : "喫煙可"}
              activeText={"禁煙"}
              isActive={smoking === Smoking.NotAllow}
            />
          </div>
        </div>
        <div
          className={`${styles["f-facilityDetail__info-point__frame"]} ${styles["bottom"]}`}
        ></div>
      </div>
      <div className={styles["f-facilityDetail__info"]}>
        <div
          className={styles["f-facilityDetail__info-message"]}
          id="pageLink-message"
        >
          <div className={styles["inner"]}>
            <ContentTitle
              icon={["fas", "comment-alt-smile"]}
              title="施設紹介"
              subTitle="MESSAGE"
            />
            <div className={styles["f-facilityDetail__info-message__text"]}>
              <p>
                {introduction
                  ? introduction
                  : "まだ情報が入力されていないようです。施設からの情報が届くのをお待ちください。"}
              </p>
            </div>
          </div>
        </div>

        {sns?.youtube ? (
          <div className={styles["f-facilityDetail__info-media"]}>
            <div className={styles["inner"]}>
              <ContentTitle
                icon={["fas", "globe"]}
                title="メディア"
                subTitle="MEDIA"
              />
              <YouTubeChannelContainer
                link={sns.youtube}
                pageType="facility"
                contentId={id}
              />
            </div>
          </div>
        ) : null}
        <div className={styles["f-facilityDetail__user-contact"]}>
          <div className={styles["inner"]}>
            <UserContact />
          </div>
        </div>
      </div>

      <div className={styles["f-facilityDetail__contact-area"]}>
        <div
          className={`${styles["f-facilityDetail__info-point__frame"]} ${styles["top"]}`}
        ></div>
        <div className={styles["f-facilityDetail__request"]}>
          <FacilityContact facilityName={name} />
        </div>
        <div className={styles["f-facilityDetail__request"]}>
          <ContactBanner />
        </div>

        {officialSite && (
          <div
            className={`${styles["f-facilityDetail__request"]} ${styles["official"]}`}
          >
            <div className={styles["f-facilityDetail__info-hp"]}>
              <a href={officialSite} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={["fas", "external-link-alt"]} />
                <span>施設へお問い合わせする</span>
              </a>
            </div>
          </div>
        )}
      </div>

      {relatedFacilities?.length ? (
        <FacilityComparison facilities={relatedFacilities} source={name} />
      ) : null}
    </div>
  );
};

export default FacilityDetail;
